import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
	Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { DeleteJobModal } from "@/components/Organisms/DeleteJobModal/DeleteJobModal";
import { DetailsState } from "@/components/Organisms/Details/Details";
import { JobSidebarDetails } from "@/components/Organisms/JobSidebarDetails/JobSidebarDetails";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import {
	JobFormSaveProvider,
	useJobFormSaveRegistry,
} from "@/features/jobs/components/job-form-save-context";
import {
	commitAutosaveOrStay,
	useJobDetailsSaveFailLabels,
} from "@/features/jobs/hooks/use-job-details-navigation-guard";
import { useJobFormNavigationGuard } from "@/features/jobs/hooks/use-job-form-navigation-guard";
import {
	getWorkspaceTabId,
	WorkspaceTab,
	type WorkspaceTabId,
} from "@/features/jobs/lib/get-workspace-tab-id.util";
import { isNestedAssetWorkspacePath } from "@/features/jobs/lib/is-nested-asset-workspace-path.util";
import { isNestedRecordWorkspacePath } from "@/features/jobs/lib/is-nested-record-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { jobQueryOptions } from "@/services/job";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Job Workspace shell — second-level entity page (Landing → Drawer → Workspace).
 * Header: PageHeader with section tabs, Options overflow (delete), and collapse back to drawer.
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace",
)({
	component: JobWorkspaceLayout,
});

function JobWorkspaceLayout() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (
		isNestedRecordWorkspacePath(pathname) ||
		isNestedAssetWorkspacePath(pathname)
	) {
		return <Outlet />;
	}

	return (
		<JobFormSaveProvider>
			<JobWorkspaceLayoutInner />
		</JobFormSaveProvider>
	);
}

function JobWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getWorkspaceTabId(pathname);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useJobFormSaveRegistry();
	const saveFailLabels = useJobDetailsSaveFailLabels();
	useJobFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const jobById = useQuery(
		jobQueryOptions.detail(params.jobId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteJobs = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteJob",
			entityType: "Job",
			entityId: params.jobId,
		}),
		enabled: !!params.jobId,
	});

	const title = jobById.data?.name ?? t("main.jobs.workspace.title");

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: WorkspaceTab.Form,
				label: t("jobs.landing.sections.form"),
			},
			{
				id: WorkspaceTab.Records,
				label: t("main.jobs.workspace.tabs.records"),
			},
			{
				id: WorkspaceTab.Assets,
				label: t("jobs.landing.sections.assets"),
			},
			{
				id: WorkspaceTab.Attachments,
				label: t("jobs.landing.sections.attachments"),
			},
			{
				id: WorkspaceTab.Notes,
				label: t("jobs.landing.sections.notes"),
			},
			{
				id: WorkspaceTab.Map,
				label: t("jobs.landing.sections.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const job = jobById.data;
		if (!job) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (job.jobNumber != null) {
			items.push({
				label: String(job.jobNumber),
				icon: Hash01,
			});
		}
		if (job.createdAt) {
			items.push({
				label: createdMetadataLabel(t, job.createdAt),
				icon: Calendar,
			});
		}
		if (job.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, job.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [jobById.data, t]);

	const handleBackToJobs = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/jobs",
				params: { orgId: params.orgId },
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/jobs/$jobId/details",
				params: { orgId: params.orgId, jobId: params.jobId },
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as WorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				to: `/web/$orgId/jobs/$jobId/workspace/${next}`,
				params: { orgId: params.orgId, jobId: params.jobId },
			});
		});
	};

	const handleDelete = () => {
		setIsDeleteModalOpen(true);
	};

	const sidebarLabel = t("main.jobs.workspace.sidebarLabel");

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="job"
							title={title}
							breadcrumbType="single"
							breadcrumbs={[
								{
									label: t("main.jobs.workspace.backToJobs"),
									onPress: handleBackToJobs,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={
								canDeleteJobs.data
									? [
											{
												label: t("main.jobs.drawer.deleteJob.button"),
												icon: Trash01,
												onPress: handleDelete,
											},
										]
									: []
							}
							actions={[
								{
									label: t("main.jobs.workspace.action"),
									"aria-label": t("main.jobs.workspace.collapse"),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<JobSidebarDetails
					orgId={params.orgId}
					jobId={params.jobId}
					state={
						jobById.isPending ? DetailsState.Loading : DetailsState.Default
					}
				/>
			</Sidebar>
			<DeleteJobModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={() => {
					setIsDeleteModalOpen(false);
					navigate({
						to: "/web/$orgId/jobs",
						params: { orgId: params.orgId },
						replace: true,
					});
				}}
			/>
		</div>
	);
}
